<template>
  <div>
    <template v-if="isTruncated && !expanded">
      <span :itemprop="itemprop || undefined">{{ truncatedPlainText }}</span>
      <button
        type="button"
        class="ml-1 inline font-semibold text-teal-600 hover:underline"
        :data-test-id="readMoreTestId"
        @click="expand"
      >
        {{ t('general.kftReadMore.readMoreLabel') }}
      </button>
    </template>
    <template v-else>
      <span
        class="html-read-more__rich"
        :itemprop="itemprop || undefined"
        v-html="safeHtml"
      />
      <button
        v-if="isTruncated"
        type="button"
        class="ml-1 inline font-semibold text-teal-600 hover:underline"
        :data-test-id="readLessTestId"
        @click="collapse"
      >
        {{ t('general.kftReadMore.readLessLabel') }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { stripHtmlToPlainText } from '~/utils/parseHtmlList'

const {
  html,
  maxChars = 190,
  defaultExpanded = false,
  testIdPrefix,
  itemprop,
} = defineProps<{
  /** Rich HTML; non-strings are ignored (API fields may not be strings at runtime). */
  html: unknown
  maxChars?: number
  /** Start expanded (still collapsible when truncated). */
  defaultExpanded?: boolean
  /** Prefix for `*-read-more` / `*-read-less` data-test-id attributes. */
  testIdPrefix?: string
  /** Optional schema.org itemprop on the text node. */
  itemprop?: string
}>()

const { t } = useI18n()

const expanded = ref(defaultExpanded)

/** API fields are typed as string but can be null/non-string at runtime. */
const safeHtml = computed(() => (typeof html === 'string' ? html : ''))

const plainText = computed(() => stripHtmlToPlainText(safeHtml.value))

const isTruncated = computed(() => plainText.value.length > maxChars)

const truncatedPlainText = computed(
  () => `${plainText.value.slice(0, maxChars).trimEnd()}…`
)

const readMoreTestId = computed(() =>
  testIdPrefix ? `${testIdPrefix}-read-more` : undefined
)

const readLessTestId = computed(() =>
  testIdPrefix ? `${testIdPrefix}-read-less` : undefined
)

const expand = () => {
  expanded.value = true
}

const collapse = () => {
  expanded.value = false
}
</script>

<style scoped lang="postcss">
.html-read-more__rich :deep(p) {
  @apply m-0 inline;
}

.html-read-more__rich :deep(p + p) {
  @apply mt-2 block;
}
</style>
